import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import CheckoutButton from "@/components/CheckoutButton";
import PromoCodeInput from "@/components/PromoCodeInput";
import { useAuth } from "@/lib/AuthContext";
import api from "@/lib/api";
import { Check } from "lucide-react";

/**
 * Offered once, right after signup, from the member Overview. Two states
 * in one dialog: the membership offer, and the reassurance shown when the
 * member defers — declining stays in place rather than firing a toast.
 *
 * The promo is previewed under the "signup" context because
 * /promo-codes/validate is unauthenticated and so cannot apply the
 * per-caller rule that lets a member spend their own signup-scoped code
 * at membership checkout.
 */
export default function SignupMembershipDialog({ open, onOpenChange }) {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [plan, setPlan] = useState(null);
    const [yearly, setYearly] = useState(false);
    const [promo, setPromo] = useState(() => user?.signup_promo_code || "");
    const [declined, setDeclined] = useState(false);

    useEffect(() => {
        if (!open) return;
        api.get("/member/subscriptions")
            .then((r) => setPlan(r.data.current_plan))
            .catch(() => {});
    }, [open]);

    if (declined) {
        return (
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent
                    data-testid="signup-offer-declined"
                    className="max-w-md bg-white"
                >
                    <DialogHeader>
                        <DialogTitle className="font-display text-xl text-navy-700">
                            {t("signup_offer.declined_title")}
                        </DialogTitle>
                        <DialogDescription className="text-navy-500">
                            {t("signup_offer.declined_body")}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            onClick={() => onOpenChange(false)}
                            data-testid="signup-offer-okay"
                            className="rounded-full bg-navy-700 hover:bg-navy-800 text-white px-8"
                        >
                            {t("signup_offer.okay")}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        );
    }

    const monthlyPrice = plan
        ? yearly
            ? Math.round(plan.price_yearly / 12)
            : plan.price_monthly
        : null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="signup-offer-dialog"
                className="max-w-md bg-white"
            >
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl text-navy-700 pr-6">
                        {t("signup_offer.title")}
                    </DialogTitle>
                    <DialogDescription className="text-navy-500">
                        {t("signup_offer.subtitle")}
                    </DialogDescription>
                </DialogHeader>

                <div className="rounded-2xl bg-navy-700 text-white p-6 my-2">
                    <div className="inline-flex items-center rounded-full bg-white/10 p-1 mb-4">
                        <button
                            type="button"
                            data-testid="signup-offer-monthly"
                            onClick={() => setYearly(false)}
                            className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${
                                !yearly
                                    ? "bg-gold-500 text-navy-900"
                                    : "text-navy-50/70 hover:text-white"
                            }`}
                        >
                            {t("signup_offer.monthly")}
                        </button>
                        <button
                            type="button"
                            data-testid="signup-offer-yearly"
                            onClick={() => setYearly(true)}
                            className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${
                                yearly
                                    ? "bg-gold-500 text-navy-900"
                                    : "text-navy-50/70 hover:text-white"
                            }`}
                        >
                            {t("signup_offer.yearly")}{" "}
                            <span className="text-gold-400 ml-1">
                                {t("signup_offer.yearly_hint")}
                            </span>
                        </button>
                    </div>

                    <div className="font-display text-5xl">
                        {monthlyPrice != null ? `$${monthlyPrice}` : "—"}
                        <span className="text-sm font-sans text-navy-50/60">
                            {" "}
                            {t("signup_offer.per_month")}
                        </span>
                    </div>
                    {plan?.tagline && (
                        <div className="text-navy-50/70 text-sm mt-1">
                            {plan.tagline}
                        </div>
                    )}
                    {plan?.features?.length > 0 && (
                        <ul className="mt-4 space-y-2 text-xs text-navy-50/90">
                            {plan.features.slice(0, 3).map((f) => (
                                <li key={f} className="flex gap-2">
                                    <Check
                                        size={14}
                                        className="text-gold-500 mt-0.5 flex-shrink-0"
                                    />
                                    {f}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <PromoCodeInput
                    context="signup"
                    value={promo}
                    onApplied={setPromo}
                    testidPrefix="signup-offer-promo"
                />

                <DialogFooter className="mt-2 flex-col sm:flex-col gap-2">
                    <CheckoutButton
                        kind="membership"
                        interval={yearly ? "yearly" : "monthly"}
                        customerEmail={user?.email}
                        userId={user?.id}
                        promoCode={promo || undefined}
                        testid="signup-offer-subscribe"
                        className="w-full rounded-full bg-gold-500 hover:bg-gold-600 text-navy-900 h-11 font-semibold"
                    >
                        {t("signup_offer.subscribe")}
                    </CheckoutButton>
                    <Button
                        type="button"
                        variant="ghost"
                        onClick={() => setDeclined(true)}
                        data-testid="signup-offer-later"
                        className="w-full text-navy-500 hover:text-navy-700 rounded-full"
                    >
                        {t("signup_offer.later")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
